import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Constants } from '../models/constants.model';

/**
 * Provides access to the application configuration. 
 * 
 * The configuration can be supplied via an appConfig.json, 
 * or as a global object named "_RoomReservationConfig".
 * 
 * "useHashRouting" and "assetPath" can only be configured when 
 * using the _RoomReservationConfig object.
 *
 * @export
 * @class AppConfigService
 */
@Injectable({
  providedIn: 'root'
})
export class AppConfigService {

  static settings: IAppConfig;

  constructor(private http: HttpClient) { }

  private static get globalConfig(): IAppConfig {
    return (window as any)._RoomReservationConfig || {};
  }

  private static get hasGlobalConfig(): boolean {
    return (window as any)._RoomReservationConfig !== undefined;
  }

  public static get translationsPath(): string {
    if (AppConfigService.hasGlobalConfig) {
      return AppConfigService.globalConfig.assetPath + 'i18n/';
    } else {
      return Constants.Urls.Translations;
    }
  }

  /**
   * Returns the oauth server url without trailing slash
   *
   * @readonly
   * @static
   * @type {string}
   * @memberof AppConfigService
   */
  public static get oauthUrl(): string {
    return AppConfigService.settings.oauthUrl.replace(/[/]*$/, '');
  }

  private static get assetPath(): string {
    if (AppConfigService.hasGlobalConfig) {
      return AppConfigService.globalConfig.assetPath;
    } else {
      return Constants.Urls.Assets;
    }
  }

  load(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (AppConfigService.hasGlobalConfig) {
        AppConfigService.settings = AppConfigService.globalConfig;
        resolve();
      } else {
        this.http.get<IAppConfig>(Constants.Urls.AppConfig)
          .toPromise<IAppConfig>()
          .then((value: IAppConfig) => {
            AppConfigService.settings = value;
            AppConfigService.settings.assetPath = Constants.Urls.Assets;
            resolve();
          })
          .catch((reason: any) => {
            reject(`Could not load file '${Constants.Urls.AppConfig}': ${JSON.stringify(reason)}`);
          });
      }
    });
  }
}

/**
 * Interface of appConfig.json
 *
 * @export
 * @interface IAppConfig
 */
export interface IAppConfig {
  /**
   * OAuth token type
   * usually: urn:ietf:params:oauth:token-type:jwt-bearer
   * @type {string}
   * @memberof IAppConfig
   */
  tokenType: string;

  /**
   * url of the web-module
   *
   * @type {string}
   * @memberof IAppConfig
   */
  webBaseUrl: string;

  /**
   * url of the REST-Api
   *
   * @type {string}
   * @memberof IAppConfig
   */
  apiUrl: string;

  /**
   * url of the Evento Authorization Server
   *
   * @type {string}
   * @memberof IAppConfig
   */
  oauthUrl: string;

  /**
   * OAuth consumer id
   *
   * @type {string}
   * @memberof IAppConfig
   */
  clientId: string;

  /**
   * Evento instance id (tenant)
   *
   * @type {string}
   * @memberof IAppConfig
   */
  instanceId: string;

  /**
   * ApplicationScope
   *
   * @type {string}
   * @memberof IAppConfig
   */
  appScope: string;

  useAutoLogin: boolean;

  useLanguageDetection: boolean;

  /**
   * debug mode
   *
   * @type {boolean}
   * @memberof IAppConfig
   */
  debug: boolean;

  /**
   * Available languages (eg. de-CH)
   * first value is primary
   *
   * @type {string}
   * @memberof IAppConfig
   */
  availableLanguages: string[];

  /**
   * Copyright (legal organization)
   *
   * @type {string}
   * @memberof IAppConfig
   */
  copyright: string;

  /**
   * Base url of the installed Room reservation
   * Used as base-url for the QR-code in RoomInfoscreen
   *
   * @type {string}
   * @memberof IAppConfig
   */
  qrCodeBaseUrl: string;

  /**
   * Enable/disable room-reservation
   *
   * @type {boolean}
   * @memberof IAppConfig
   */
  showRoomReservation: boolean;

  /**
   * Enable/disable device-reservation
   *
   * @type {boolean}
   * @memberof IAppConfig
   */
  showDeviceReservation: boolean;

  /**
   * Use HashLocationStrategy. 
   * 
   * This config can only be set via the global 
   * _RoomReservationConfig object.
   * 
   * @type {boolean}
   * @memberof IAppConfig
   */
  useHashRouting: boolean;

  /**
   * Use HashLocationStrategy
   * 
   * This config can only be set via the global
   * _RoomReservationConfig object.
   * 
   * @type {boolean}
   * @memberof IAppConfig
   */
  assetPath: string;

  /**
   * Pre-Check ShowAvailableRooms checkbox
   * 
   * @type {boolean}
   * @memberof IAppConfig
   */
  ShowAvailableRoomsChecked: boolean;

  /** 
   * Default reservation duration config value
   * 
   * @type {number}
   * @memberof IAppConfig
   */
  DefaultTimePeriod: number;

  /**
   * Default time steps config value
   * 
   * @type {number}
   * @memberof IAppConfig
   */
  DefaultTimeFragmentation: number;

  /**
   * Enable or disable serial reservation feature
   *  
   * @type {boolean}
   * @memberof IAppConfig
   */
  AllowFurtherEvents: boolean;

  /**
   * Specify which columns should be shown in the calender view
   * 
   * @type {{Column: string, Size: number}[]}
   */
  RoomColumns: { Column: string, Size: number }[];

}
